import type { FreebetPolicy, Money, Odds, PromoMathPolicy, SignedMoney } from '@urbanrats/contracts';
import { atomicOf, money, signedMoney } from './money.ts';
import { computeWinPayout } from './payout.ts';
import { err, ok, type Result } from './result.ts';

export type BetFunding = { kind: 'CASH' } | { kind: 'FREEBET'; faceValue: Money; policy: FreebetPolicy };

export interface PromoPayoutInput {
  stake: Money;
  odds: Odds;
  lightning: Odds | null;
  boostBps: number;
  cap: Money | null;
  funding: BetFunding;
}

export interface PromoPayout {
  /** Formula result after the cap (total return as if the stake were cash). */
  gross: Money;
  /** Amount credited to the wallet. */
  credit: Money;
  /** Wallet effect for the player: credit − cash debit. */
  net: SignedMoney;
  capped: boolean;
}

/**
 * Settlement of one bet under PromoMathPolicy (single formula, single final floor, then the cap).
 * A freebet debits no cash: STAKE_INCLUDED credits the full gross; PROFIT_ONLY credits gross − face value.
 * A loss credits zero (the wallet round still closes with a zero-amount result).
 */
export function promoPayout(input: PromoPayoutInput, won: boolean): PromoPayout {
  const { stake, funding } = input;
  const cashDebit = funding.kind === 'CASH' ? atomicOf(stake) : 0n;
  if (!won) {
    const zero = money(0n, stake.currency, stake.scale);
    return { gross: zero, credit: zero, net: signedMoney(-cashDebit, stake.currency, stake.scale), capped: false };
  }
  const p = computeWinPayout({ stake, odds: input.odds, lightning: input.lightning, boostBps: input.boostBps, cap: input.cap });
  const gross = atomicOf(p.gross);
  let credit = gross;
  if (funding.kind === 'FREEBET' && funding.policy === 'PROFIT_ONLY') {
    const face = atomicOf(funding.faceValue);
    credit = gross > face ? gross - face : 0n;
  }
  return { gross: p.gross, credit: money(credit, stake.currency, stake.scale), net: signedMoney(credit - cashDebit, stake.currency, stake.scale), capped: p.capped };
}

export type StackingError = { code: 'PROMO_NOT_STACKABLE'; message: string };

/** Which promotions may combine on one bet, per the policy in force. */
export function checkStacking(
  policy: PromoMathPolicy,
  p: { rewards: { kind: 'FREEBET' | 'BOOST'; boostBps: number | null }[]; lightning: boolean },
): Result<{ boostBps: number }, StackingError> {
  if (p.rewards.length > policy.maxRewardsPerBet) return err({ code: 'PROMO_NOT_STACKABLE', message: `at most ${policy.maxRewardsPerBet} reward(s) per bet` });
  const freebet = p.rewards.some((r) => r.kind === 'FREEBET');
  const boostBps = p.rewards.reduce((a, r) => a + (r.kind === 'BOOST' ? (r.boostBps ?? 0) : 0), 0);
  if (boostBps > policy.maxBoostBpsPerBet) return err({ code: 'PROMO_NOT_STACKABLE', message: 'boost above the per-bet maximum' });
  if (p.lightning && freebet && !policy.freebetWithLightning) return err({ code: 'PROMO_NOT_STACKABLE', message: 'freebets do not combine with Lightning' });
  if (p.lightning && boostBps > 0 && !policy.boostWithLightning) return err({ code: 'PROMO_NOT_STACKABLE', message: 'boosts do not combine with Lightning' });
  return ok({ boostBps });
}
